import React, { useRef, useEffect, useState } from "react";
import { Box, Typography, Grid, Paper, Fade } from "@mui/material";
import EditNoteIcon from "@mui/icons-material/EditNote";
import SchemaIcon from "@mui/icons-material/Schema";
import DrawIcon from "@mui/icons-material/Draw";
import GroupIcon from "@mui/icons-material/Group";

const vibrantPurple = "#9575CD";
const vibrantBlue = "#64B5F6";
const vibrantOrange = "#FFB74D";
const vibrantGreen = "#81C784";

const keyFunctionIconStyle = {
  fontSize: { xs: "medium", md: 40 },
};

const features = [
  {
    icon: <EditNoteIcon sx={{ color: vibrantPurple }} />,
    title: "Easy Annotation",
    description: "Effortlessly highlight text, add comments, and tag arguments directly on any webpage or document. Capture your thoughts and evidence precisely where you find them.",
  },
  {
    icon: <SchemaIcon sx={{ color: vibrantBlue }} />,
    title: "Smart Diagrams",
    description: "Instantly generate interactive argument maps from your annotations. Visualize complex relationships, identify logical structures, and gain new perspectives on the material.",
  },
  {
    icon: <DrawIcon sx={{ color: vibrantOrange }} />,
    title: "Focused Writing",
    description: "Seamlessly integrate your research and diagrams into a dedicated writing space. Draft, outline, and refine your arguments using powerful tools designed for clarity and structure.",
  },
  {
    icon: <GroupIcon sx={{ color: vibrantGreen }} />,
    title: "Effortless Collaboration",
    description: "Share your projects, co-annotate documents, and build arguments together in real-time. Foster productive discussions and leverage collective intelligence seamlessly.",
  },
];

const featureCardIconStyle = {
  fontSize: { xs: "medium", md: 40 },
};


const keyFunctions = [
  {
    label: "Annotate",
    icon: <EditNoteIcon sx={{ ...keyFunctionIconStyle, color: vibrantPurple }} />,
  },
  {
    label: "Diagram",
    icon: <SchemaIcon sx={{ ...keyFunctionIconStyle, color: vibrantBlue }} />,
  },
  {
    label: "Write",
    icon: <DrawIcon sx={{ ...keyFunctionIconStyle, color: vibrantOrange }} />,
  },
  {
    label: "Collaborate",
    icon: <GroupIcon sx={{ ...keyFunctionIconStyle, color: vibrantGreen }} />,
  },
];

// One clip per feature card (indices align with `features` above). Each
// file in public/landing is named for the feature it demos.
const featureVideos = [
  "/landing/Easy_Annotation.mp4", // Easy Annotation
  "/landing/Smart_Diagrams.mp4", // Smart Diagrams
  "/landing/Focused_Writing.mp4", // Focused Writing
  "/landing/Effortless_Collaboration.mp4", // Effortless Collaboration
];


const Features = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const featureRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const cards = featureRefs.current;

    // Drive the active feature from whichever description sits at the
    // viewport's vertical centre. IntersectionObserver does the geometry
    // off the scroll path (no per-frame getBoundingClientRect), and the
    // thin centre band means the video only switches as the next
    // description scrolls into the middle — not the moment a card peeks
    // in at the edge.
    const observer = new IntersectionObserver(
      (entries) => {
        const inBand = entries
          .filter((entry) => entry.isIntersecting)
          .map((entry) => cards.indexOf(entry.target as HTMLDivElement))
          .filter((index) => index !== -1);
        // If two cards briefly straddle the band, prefer the topmost so
        // the transition lands on a single, stable choice.
        if (inBand.length > 0) setActiveIndex(Math.min(...inBand));
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );

    cards.forEach((card) => card && observer.observe(card));
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      id="features-section"
      sx={{
        py: { xs: 6, md: 10 },
        px: { xs: 2, md: 0 },
        position: "relative",
        minHeight: "80vh",
        // Without a cap the grid tracks the viewport, so on wide
        // monitors the description column keeps growing while the
        // video stays pinned to its maxWidth — which is what made the
        // text look dominant. Capping holds the intended ratio.
        maxWidth: 1400,
        mx: "auto",
      }}
    >
      <Typography variant="h4" align="center" gutterBottom sx={{ fontSize: { xs: '1.8rem', md: '2.125rem' }, mb: { xs: 4, md: 6 } }}>
        The easiest way to annotate, diagram, write, and collaborate online.
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: { xs: 3, md: 6 },
          mb: { xs: 4, md: 6 },
        }}
      >
        {keyFunctions.map((func) => (
          <Box
            key={func.label}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              minWidth: { xs: 70, md: 90 },
            }}
          >
            {func.icon}
            <Typography variant="caption" sx={{ mt: 1, textAlign: 'center' }}>
              {func.label}
            </Typography>
          </Box>
        ))}
      </Box>
      <Grid container spacing={{ xs: 2, md: 4 }} justifyContent="center" sx={{ position: "relative" }}>
        {/* Descriptions yield width to the demo at lg; they stay 50/50
            at md, where the narrower column can't afford to shrink. */}
        <Grid item xs={12} md={6} lg={5}>
          <Box>
            {features.map((feature, idx) => (
              <div
                key={idx}
                ref={(el) => (featureRefs.current[idx] = el)}
                style={{ minHeight: "30vh", marginBottom: "1.5rem" }}
              >
                <Fade in={activeIndex === idx} timeout={600} unmountOnExit>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 2, md: 3 },
                      textAlign: "left",
                      display: "flex",
                      alignItems: "center",
                      minHeight: { xs: 'auto', md: 150 },
                      // Match the framed video's shape language (16px corners
                      // + a hairline border), but with a lighter shadow than
                      // the video's so the demo stays the focal point. Stays
                      // theme-adaptive (Paper bg + divider follow the theme).
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      boxShadow: 2,
                    }}
                  >
                    <Box sx={{ mr: 2, display: "flex", alignItems: "center" }}>
                      {React.cloneElement(feature.icon, {
                        sx: { ...feature.icon.props.sx, ...featureCardIconStyle }
                      })}
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ mt: 0, fontSize: { xs: '1.1rem', md: '1.5rem'} }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                        {feature.description}
                      </Typography>
                    </Box>
                  </Paper>
                </Fade>
              </div>
            ))}
          </Box>
        </Grid>
        <Grid
          item
          md={6}
          lg={7}
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "sticky",
              top: 100,
              alignSelf: "flex-start",
              width: "100%",
              zIndex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {activeIndex >= 0 && activeIndex < featureVideos.length && (
               <Fade key={activeIndex} in={true} timeout={1000}>
                 <Box
                   sx={{
                     width: { md: '100%' },
                     // Capped short of the clips' native 1280px so the
                     // demo grows without visible upscaling.
                     maxWidth: { md: 620, lg: 720 },
                     // Matches the source clips (1280x698). A 16/9 frame
                     // letterboxed every one of them.
                     aspectRatio: '1280 / 698',
                     p: 0.5,
                     borderRadius: 2,
                     overflow: 'hidden',
                     // Fixed dark "app window" chrome so a dark-recorded clip
                     // reads as an intentional screenshot in both the light
                     // and dark landing themes. Record demos in dark to
                     // match; if you record in light, flip bgcolor to a light
                     // neutral and borderColor to a darker hairline.
                     bgcolor: '#1b1b1f',
                     border: '1px solid',
                     borderColor: 'rgba(255, 255, 255, 0.12)',
                     boxShadow: 6,
                   }}
                 >
                   <video
                     src={featureVideos[activeIndex]}
                     autoPlay
                     loop
                     muted
                     playsInline
                     // contain = never crop; any letterbox fills with the
                     // card's dark chrome. (cover would crop off-ratio clips.)
                     style={{
                       width: '100%',
                       height: '100%',
                       objectFit: 'contain',
                       display: 'block',
                       borderRadius: '6px',
                     }}
                   />
                 </Box>
               </Fade>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Features;